import { type ErrorComponentProps, useNavigate } from "@tanstack/react-router";
import { Heading1, Paragraph } from "../Typography";
import { Button } from "../ui/button";
import errorLogo from "@/assets/cow-spaceship.svg";
import { getErrorMessage } from "@/lib/utils/errors";
import { type FileRoutesByTo } from "@/routeTree.gen";

interface ErrorComponentPropsExtended extends ErrorComponentProps {
  title: string;
  message: string;
  backTo?: {
    path: keyof FileRoutesByTo;
    text: string;
  };
}

export function ErrorComponent({ error, reset, title, message, backTo }: ErrorComponentPropsExtended) {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-grow-[1]" />
      <div className="flex flex-col items-center flex-shrink-0">
        <img src={errorLogo} alt="Error" width="185" height="180" />
        <div className="px-2 text-center">
          <Heading1 className="text-2xl">{title}</Heading1>
          <Paragraph className="my-2 block">{message}</Paragraph>
          <pre className="text-red-500 text-sm md:text-base whitespace-pre-wrap break-words p-2 rounded bg-red-50 mt-4">
            {getErrorMessage(error)}
          </pre>
        </div>
        <div className="flex gap-4 mt-6">
          <Button className="text-md" size={"lg"} variant={backTo ? "secondary" : "confirm"} onClick={reset}>
            Try again
          </Button>
          {backTo && (
            <Button
              className="text-md"
              size={"lg"}
              variant={"confirm"}
              onClick={() => {
                void navigate({ to: backTo.path, replace: true });
              }}
            >
              {backTo.text}
            </Button>
          )}
        </div>
      </div>
      <div className="flex-grow-[4]" />
    </div>
  );
}
